import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  ButtonGroup,
  Button,
} from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// Dados atualizados conforme a tabela completa de 2025 e 2026
const dadosPorAno = {
  2025: {
    meses: [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ],
    materialColetado: [
      58690, 33560, 53400, 29710, 48390, 38740, 80570, 49130, 46570, 93020,
      79960, 62500,
    ],
    rejeito: [
      11960, 10070, 9310, 8910, 10200, 10220, 8710, 7840, 8150, 8000, 8787,
      11316,
    ],
    valoresRecebidos: [
      22114.4, 22868, 16948, 24629, 16467, 29512.75, 38615, 49613.12, 49461.9,
      72169.0, 69664.5, 38781.95,
    ],
    despesas: [
      6546.89, 6018.45, 5955, 5637.42, 4728.72, 6150.18, 7943.98, 12064.82,
      20667.0, 7294.62, 10281.98, 10410.96,
    ],
    valorRateio: [
      15567.51, 16849.55, 10993, 18991.58, 11738.28, 23362.57, 30671.02,
      37548.3, 28794.9, 64874.38, 59382.52, 28370.99,
    ],
  },
  2026: {
    meses: [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ],
    // Meses de Jan a Jul preenchidos; o resto permanece zerado
    materialColetado: [
      55400, 63680, 85880, 78250, 105350, 92250, 84150, 0, 0, 0, 0, 0,
    ],
    rejeito: [7930, 9590, 8760, 8580, 7480, 8800, 7710, 0, 0, 0, 0, 0],
    valoresRecebidos: [
      35768, 50629, 59418.7, 55180.8, 78506.0, 75522.7, 70791.8, 0, 0, 0, 0, 0,
    ],
    despesas: [
      12430.75, 12879.02, 11881.93, 14740.89, 22057.01, 12109.16, 12819.34, 0,
      0, 0, 0, 0,
    ],
    valorRateio: [
      23337.25, 37749.98, 47536.77, 40439.91, 56448.99, 63413.54, 57972.46, 0,
      0, 0, 0, 0,
    ],
  },
};

export default function Graficos() {
  const [anoSelecionado, setAnoSelecionado] = useState("2026");

  const { data, dataValores, options, optionsValores } = useMemo(() => {
    const resultados = dadosPorAno[anoSelecionado];

    const meses = resultados.meses;
    const totalPercent = Array(meses.length).fill(100);
    const total = resultados.materialColetado;
    const rejeicao = resultados.rejeito;

    const rejeicaoPercent = rejeicao.map((v, i) =>
      total[i] > 0 ? ((v / total[i]) * 100).toFixed(2) : 0,
    );

    const chartData = {
      labels: meses,
      datasets: [
        {
          label: "Total Coletado (%)",
          data: totalPercent,
          backgroundColor: "#007bff",
          stack: "Stack 0",
          barPercentage: 0.5,
          categoryPercentage: 0.5,
          barThickness: 20,
        },
        {
          label: "Rejeição (%)",
          data: rejeicaoPercent,
          backgroundColor: "#dc3545",
          stack: "Stack 1",
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      plugins: {
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: ${context.parsed.y}%`;
            },
          },
        },
      },
      scales: {
        x: { stacked: true },
        y: {
          stacked: true,
          beginAtZero: true,
          max: 100,
          title: { display: true, text: "%" },
          ticks: {
            callback: function (value) {
              return value + "%";
            },
          },
        },
      },
    };

    const chartDataValores = {
      labels: meses,
      datasets: [
        {
          label: "Valores Recebidos",
          data: resultados.valoresRecebidos,
          backgroundColor: "#007bff",
        },
        {
          label: "Despesas",
          data: resultados.despesas,
          backgroundColor: "#dc3545",
        },
        {
          label: "Valor para Rateio",
          data: resultados.valorRateio,
          backgroundColor: "#28a745",
        },
      ],
    };

    const chartOptionsValores = {
      responsive: true,
      plugins: {
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            label: function (context) {
              return `${
                context.dataset.label
              }: R$ ${context.parsed.y.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
              })}`;
            },
          },
        },
      },
      scales: {
        x: {},
        y: {
          beginAtZero: true,
          title: { display: true, text: "R$" },
        },
      },
    };

    return {
      data: chartData,
      dataValores: chartDataValores,
      options: chartOptions,
      optionsValores: chartOptionsValores,
    };
  }, [anoSelecionado]);

  return (
    <section id="resultados" className="py-5 bg-light">
      <Container>
        <Row className="text-center mb-4">
          <Col>
            <h2 className="display-5 fw-bold text-dark mb-3">
              Resultados da Cooperativa
            </h2>
            <p className="lead text-muted">
              Evolução mensal do material e valores
            </p>
          </Col>
        </Row>

        <Row className="mb-5 justify-content-center">
          <Col xs="auto">
            <ButtonGroup>
              <Button
                variant={
                  anoSelecionado === "2025" ? "primary" : "outline-primary"
                }
                onClick={() => setAnoSelecionado("2025")}
              >
                2025
              </Button>
              <Button
                variant={
                  anoSelecionado === "2026" ? "primary" : "outline-primary"
                }
                onClick={() => setAnoSelecionado("2026")}
              >
                2026
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        <Row className="g-4">
          <Col lg={6} md={12}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="fw-bold text-center mb-3">
                  Valores Recebidos, Despesas e Rateio (R$) - {anoSelecionado}
                </Card.Title>
                <Bar data={dataValores} options={optionsValores} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} md={12}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="fw-bold text-center mb-3">
                  Comparativo do Material: Total X Rejeitado (%) -{" "}
                  {anoSelecionado}
                </Card.Title>
                <Bar data={data} options={options} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
}
